import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { myRequests, offersByRequest, chooseOffer } from '@/api';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { MessageCircle, Eye, ArrowLeft, MapPin, Clock, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { StarRatingDisplay } from '@/components/StarRating';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';

const LOGO_URL = '/logo.png';

function Badge({status}){
  const { t } = useLanguage();
  const map = { open: 'bg-teal-100 text-teal-700', assigned: 'bg-amber-100 text-amber-700', closed: 'bg-emerald-100 text-emerald-700', canceled: 'bg-slate-100 text-slate-600' };
  const label = { open: t('ui.waiting'), assigned: t('ui.inProgress'), closed: t('ui.completed'), canceled: t('ui.canceled') }[status] || status;
  return <span className={`px-3 py-1 inline-flex items-center rounded-full text-xs font-semibold ${map[status]||map.open}`} data-testid="request-status-badge">{label}</span>;
}

function OfferRow({o, disabled, onChoose, onViewDetail}){
  const { t } = useLanguage();
  const statusColor = { pending: 'text-slate-600', won: 'text-emerald-600 font-semibold', lost: 'text-rose-600', expired: 'text-slate-400' }[o.status] || 'text-slate-600';
  const statusLabel = { pending: t('ui.pending'), won: t('ui.accepted'), lost: t('ui.rejected'), expired: 'Scaduta' }[o.status] || o.status;
  const hasRating = o.companyRating !== undefined && o.companyRating > 0;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-shadow" data-testid={`offer-${o.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <div className="text-lg font-semibold text-slate-900">EUR {Number(o.amount).toFixed(2)}</div>
          <div className={`text-sm ${statusColor}`}>Stato: {statusLabel}</div>
          {hasRating && <div className="mt-2"><StarRatingDisplay rating={o.companyRating} reviewCount={o.companyReviewCount} size="sm" /></div>}
        </div>
        <button onClick={onViewDetail} className="p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600" data-testid={`view-offer-${o.id}`}><Eye className="w-4 h-4" /></button>
      </div>
      <button data-testid={`choose-offer-${o.id}`} disabled={disabled || o.status !== 'pending'} onClick={onChoose} className={`w-full h-10 rounded-xl border-2 text-sm font-medium transition-colors ${disabled || o.status!=='pending' ? 'border-slate-200 text-slate-400 bg-slate-50' : 'border-[#5848FF] text-[#5848FF] hover:bg-indigo-50'}`}>{t('ui.chooseCompany')}</button>
      {o.status === 'pending' && !disabled && <p className="text-xs text-slate-500 text-center mt-2">{t('ui.noBinding')}</p>}
    </div>
  );
}

export default function ClientRequestDetail(){
  const { id } = useParams();
  const nav = useNavigate();
  const [req,setReq]=useState(null);
  const [offers,setOffers]=useState([]);
  const [loading,setLoading]=useState(true);
  const [offerDetailOpen, setOfferDetailOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  useEffect(()=>{
    (async()=>{
      try{ const r = await myRequests(); setReq((r.data||[]).find(x=>x.id===id)||null); }catch{ setReq(null); }
      try{ const o = await offersByRequest(id); setOffers(o.data||[]); }catch{ setOffers([]); }
      setLoading(false);
    })();
  },[id]);

  const choose = async(offerId)=>{
    try{
      await chooseOffer(id, offerId);
      toast.success('Ditta selezionata!');
      const o = await offersByRequest(id);
      setOffers(o.data||[]);
      setReq(prev => prev ? { ...prev, status:'assigned' } : prev);
    }catch(e){ toast.error(e?.response?.data?.detail||'Errore selezione'); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button onClick={()=>nav('/cliente/richieste')} className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
              <Link to="/"><img src={LOGO_URL} alt="ServiceGo" className="h-10 w-auto" /></Link>
            </div>
            <LanguageSelector />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="client-request-detail">
        <div className="mb-6">
          <button data-testid="back-to-requests" onClick={()=>nav('/cliente/richieste')} className="text-sm text-[#5848FF] hover:underline mb-2 inline-block">{t('ui.backToRequests')}</button>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t('ui.offersReceived')}</h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            {req && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sticky top-28">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <h2 className="text-lg font-semibold text-slate-900">{req.title||'Richiesta'}</h2>
                  <Badge status={req.status} />
                </div>
                <p className="text-sm text-slate-600 mb-4">{req.description}</p>
                {req.address && (
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-3"><MapPin className="w-4 h-4" />{req.address.street} {req.address.number}, {req.address.cap} {req.address.city}</div>
                )}
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-3"><Users className="w-4 h-4" />{offers.length} offerte ricevute</div>
                {req.status==='assigned' && (
                  <Button data-testid="open-chat-button" onClick={() => {
                    const wonOffer = offers.find(o => o.status === 'won');
                    if (!wonOffer || !wonOffer.companyId) { toast.error('Nessuna ditta selezionata'); return; }
                    nav(`/cliente/messaggi/${id}-${wonOffer.companyId}`);
                  }} className="w-full mt-3 bg-[#5848FF] hover:bg-[#4638EF] text-white rounded-xl">
                    <MessageCircle className="w-4 h-4 mr-2"/> Apri chat con la ditta
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            {loading ? (
              <div className="text-center py-12 text-slate-500">Caricamento...</div>
            ) : offers.length===0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center" data-testid="no-offers">
                <p className="text-slate-500">{t('ui.noOffers')}</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {offers.map(o=> (
                  <OfferRow key={o.id} o={o} disabled={req?.status!=='open'} onChoose={()=>choose(o.id)} onViewDetail={()=>{ setSelectedOffer(o); setOfferDetailOpen(true); }} />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Dialog open={offerDetailOpen} onOpenChange={setOfferDetailOpen}>
        <DialogContent className="rounded-2xl bg-white max-w-md" data-testid="offer-detail-dialog">
          <DialogHeader><DialogTitle>{t('ui.offerDetail')}</DialogTitle></DialogHeader>
          {selectedOffer && (() => {
            let offerDetails = null;
            try { offerDetails = JSON.parse(selectedOffer.note); } catch (e) {}
            return (
              <div className="max-h-[60vh] overflow-y-auto space-y-4 text-sm">
                <div><div className="text-xs text-slate-500 uppercase font-semibold mb-1">Importo</div><div className="text-3xl font-bold text-[#5848FF]">EUR {Number(selectedOffer.amount).toFixed(2)}</div></div>
                <div><div className="text-xs text-slate-500 uppercase font-semibold mb-1">Stato</div>
                  <span className={`px-3 py-1 inline-flex items-center rounded-full text-xs font-semibold ${selectedOffer.status === 'pending' ? 'bg-amber-100 text-amber-700' : selectedOffer.status === 'won' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                    {selectedOffer.status === 'pending' ? 'In attesa' : selectedOffer.status === 'won' ? 'Accettata' : selectedOffer.status === 'lost' ? 'Rifiutata' : selectedOffer.status}
                  </span>
                </div>
                {offerDetails && (
                  <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                    {offerDetails.tipo && <div className="flex justify-between"><span className="text-slate-600">Tipo:</span><span className="font-medium">{offerDetails.tipo === 'corpo' ? 'A corpo' : 'A ore'}</span></div>}
                    {offerDetails.offerta_a_corpo && <div className="flex justify-between"><span className="text-slate-600">Prezzo:</span><span className="font-medium">EUR {offerDetails.offerta_a_corpo}</span></div>}
                    {offerDetails.tariffa_oraria && <div className="flex justify-between"><span className="text-slate-600">Tariffa oraria:</span><span className="font-medium">EUR {offerDetails.tariffa_oraria}/h</span></div>}
                    {offerDetails.note && <div><span className="text-slate-600">Note:</span><p className="mt-1">{offerDetails.note}</p></div>}
                  </div>
                )}
                <div><div className="text-xs text-slate-500 uppercase font-semibold mb-1">Data invio</div><div className="text-slate-700">{new Date(selectedOffer.createdAt).toLocaleString('it-IT')}</div></div>
                <div className="flex gap-2 pt-3 border-t">
                  {selectedOffer.status === 'pending' && req?.status === 'open' && <Button onClick={() => { setOfferDetailOpen(false); choose(selectedOffer.id); }} className="flex-1 bg-[#5848FF] text-white rounded-xl">Scegli questa ditta</Button>}
                  <Button onClick={() => setOfferDetailOpen(false)} variant="outline" className="flex-1 rounded-xl">Chiudi</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
